/**
 * ValuationVideoBuilder — Post Valuation VIDEO.
 * Agent types the address, price and a personal sign-off; we trigger the
 * branch's Synthesia account and the finished video lands here + Social Library.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Loader2, Clapperboard, PoundSterling, Home, Heart, UserRound, Send, Download,
  CheckCircle2, AlertTriangle, Clock, Link2Off, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { AvatarPicker, AvatarChip } from "./AvatarPicker";
import "./ValuationFollowUp.css";
import "./ValuationVideo.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIGN_OFF_SUGGESTIONS = [
  "Lovely to meet your dog!",
  "Enjoy your trip away this weekend.",
  "Good luck with the new job.",
  "Thanks again for the tea and biscuits.",
  "Say hi to the kids for me.",
];

const STATUS_META = {
  queued: { label: "Queued", cls: "queued", Icon: Clock },
  rendering: { label: "Creating your video…", cls: "rendering", Icon: Loader2 },
  complete: { label: "Ready", cls: "complete", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "failed", Icon: AlertTriangle },
  awaiting_synthesia: { label: "Waiting for Synthesia link", cls: "awaiting", Icon: Link2Off },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.queued;
  return (
    <span className={`vv-status vv-status-${m.cls}`} data-testid={`vv-status-${status}`}>
      <m.Icon size={13} className={status === "rendering" ? "animate-spin" : ""} />
      {m.label}
    </span>
  );
}

export default function ValuationVideoBuilder({ agentName, agencyName }) {
  const [address, setAddress] = useState("");
  const [price, setPrice] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [signOff, setSignOff] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [branchStatus, setBranchStatus] = useState(null);
  const [videos, setVideos] = useState([]);
  const pollRef = useRef(null);

  const loadVideos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/valuation-video/mine`);
      setVideos(r.data?.videos || []);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/valuation-video/status`);
        setBranchStatus(r.data);
      } catch (_) { setBranchStatus({ synthesia_linked: false }); }
    })();
    loadVideos();
  }, [loadVideos]);

  // Poll while anything is rendering
  useEffect(() => {
    const active = videos.some((v) => v.status === "rendering" || v.status === "queued");
    if (active && !pollRef.current) {
      pollRef.current = setInterval(loadVideos, 10000);
    } else if (!active && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [videos, loadVideos]);

  const handleSubmit = async () => {
    if (!address.trim()) return toast.error("Please enter the property address");
    if (!price.trim()) return toast.error("Please enter the valuation price");
    if (!avatar) return toast.error("Please choose a presenter avatar");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/valuation-video/submit`, {
        address, price, sign_off: signOff, seller_name: sellerName, avatar_id: avatar.id,
      });
      const job = r.data;
      if (job.status === "awaiting_synthesia") {
        toast.message("Saved — waiting for your branch's Synthesia link", { description: job.error, duration: 7000 });
      } else if (job.status === "failed") {
        toast.error(job.error || "Synthesia couldn't start the video");
      } else {
        toast.success("Your Post Valuation video is being created");
      }
      setVideos((prev) => [job, ...prev]);
      setAddress(""); setPrice(""); setSellerName(""); setSignOff("");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to submit — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const linked = !!branchStatus?.synthesia_linked;

  return (
    <div className="vf-builder vv-builder" data-testid="valuation-video-builder">
      <div className="vf-layout">
        <div className="vf-form-col">
          <div className="vf-form-header">
            <Clapperboard size={20} />
            <div>
              <h2>Post Valuation Video</h2>
              <p>A personal avatar video for the seller, produced automatically after your appraisal.</p>
            </div>
          </div>

          {branchStatus && (
            <div className={`vv-link-banner ${linked ? "is-linked" : "not-linked"}`} data-testid="vv-link-banner">
              {linked ? <Sparkles size={14} /> : <Link2Off size={14} />}
              <span>
                {linked
                  ? `Synthesia is linked for ${branchStatus.branch_name || "your branch"}${branchStatus.test_mode ? " (test mode — watermarked)" : ""}.`
                  : "Your branch's Synthesia account isn't linked yet. You can still submit — the video will be produced as soon as NicheCom connects it."}
              </span>
            </div>
          )}

          <div className="vf-row-2">
            <div className="vf-field">
              <Label className="vf-label"><Home size={14} /> Property address *</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. 28 Beaucroft Lane, Wimborne" className="vf-input" data-testid="vv-address" />
            </div>
            <div className="vf-field">
              <Label className="vf-label"><PoundSterling size={14} /> Valuation price *</Label>
              <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. £675,000" className="vf-input" data-testid="vv-price" />
            </div>
          </div>

          <div className="vf-field">
            <Label className="vf-label"><UserRound size={14} /> Seller name(s) (optional)</Label>
            <Input value={sellerName} onChange={(e) => setSellerName(e.target.value)} placeholder="e.g. John and Sarah" className="vf-input" data-testid="vv-seller-name" />
          </div>

          <div className="vf-field">
            <Label className="vf-label"><Heart size={14} /> Personal sign-off</Label>
            <Textarea value={signOff} onChange={(e) => setSignOff(e.target.value)} placeholder="Something personal from the visit…" className="vf-textarea" rows={2} data-testid="vv-sign-off" />
            <div className="vf-chip-row">
              {SIGN_OFF_SUGGESTIONS.map((s) => (
                <button key={s} type="button" className={`vf-chip ${signOff === s ? "active" : ""}`} onClick={() => setSignOff(s)} data-testid="vv-sign-off-chip">
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="vf-field">
            <AvatarPicker value={avatar?.id} onChange={setAvatar} />
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="vf-generate-btn" data-testid="vv-submit-btn">
            {submitting ? <><Loader2 className="vf-spin" size={16} /> Sending…</> : <><Send size={16} /> Create Post Valuation Video</>}
          </Button>
          <p className="vv-fineprint">Presenter: {avatar ? avatar.name : "not chosen yet"} · From {agentName || "you"}{agencyName ? ` at ${agencyName}` : ""}</p>
        </div>

        <div className="vf-output-col">
          <div className="vv-list-head">
            <h3>Your videos</h3>
            <span>{videos.length}</span>
          </div>
          {videos.length === 0 ? (
            <div className="vv-empty" data-testid="vv-empty">
              <Clapperboard size={26} />
              <p>No Post Valuation videos yet. Fill in the details and hit create — it takes a few minutes.</p>
            </div>
          ) : (
            <div className="vv-list" data-testid="vv-list">
              {videos.map((v) => (
                <div key={v.id} className={`vv-card vv-card-${v.status}`} data-testid={`vv-card-${v.id}`}>
                  <div className="vv-card-top">
                    <div className="vv-card-title">
                      <strong>{v.address}</strong>
                      <span>{v.price}{v.seller_name ? ` · ${v.seller_name}` : ""}</span>
                    </div>
                    <StatusPill status={v.status} />
                  </div>
                  {v.avatar && <AvatarChip avatar={v.avatar} size={22} />}
                  {v.sign_off && <p className="vv-signoff">“{v.sign_off}”</p>}
                  {v.status === "complete" && v.video_url && (
                    <div className="vv-player-wrap">
                      <video src={v.video_url} controls playsInline className="vv-player" data-testid={`vv-player-${v.id}`} />
                      <a className="vv-download" href={v.video_url} download target="_blank" rel="noreferrer" data-testid={`vv-download-${v.id}`}>
                        <Download size={14} /> Download MP4
                      </a>
                    </div>
                  )}
                  {(v.status === "failed" || v.status === "awaiting_synthesia") && v.error && (
                    <p className="vv-error" data-testid={`vv-error-${v.id}`}>{v.error}</p>
                  )}
                  <span className="vv-time">{new Date(v.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
